// Shared, platform-agnostic presentation helpers for the briefing weather
// card. Web (lucide-react) and mobile (lucide-react-native) render different
// icon *components*, so this module only decides the SEMANTICS (which icon
// key, how to format a temperature / rain chance) and the timeline
// composition. Each platform maps the icon key to its own component and owns
// only its collapse-state storage. Keeps the weather knowledge in one place
// instead of duplicated across two cards.

import { doorsForDay, type DayDoors, type DoorsWindow } from "./doorsForDay";
import { formatShiftDay } from "./formatters";

/** The per-day forecast shape stored in briefing_weather.daily by the
 * refresh-weather edge function. */
export interface WeatherDay {
  date: string; // ISO "YYYY-MM-DD"
  weatherCode: number;
  tempMax: number | null;
  tempMin: number | null;
  precipitationSumMm: number | null;
  precipitationProbabilityMax: number | null;
}

/** One hour on the display timeline (icon + temp + rain%). */
export interface HourlyForecast {
  time: string; // local ISO "YYYY-MM-DDTHH:MM"
  date: string; // "YYYY-MM-DD"
  label: string; // "HH:MM"
  utcMs: number; // absolute instant, to hide past hours
  temperature: number | null;
  weatherCode: number;
  precipitationProbability: number | null;
  precipitationMm: number | null;
}

/** The full cached weather row for a briefing. */
export interface BriefingWeather {
  briefing_id: string;
  latitude: number;
  longitude: number;
  coord_source: string;
  daily: WeatherDay[];
  hourly: HourlyForecast[];
  summary: string | null;
  fetched_at: string;
}

/** A festival day's hours, grouped for the timeline. */
export interface HourlyDayGroup {
  date: string;
  hours: HourlyForecast[];
}

/** Group the flat hourly list into per-day buckets, preserving order. */
export function groupHoursByDay(hours: HourlyForecast[]): HourlyDayGroup[] {
  const groups: HourlyDayGroup[] = [];
  for (const h of hours) {
    let g = groups[groups.length - 1];
    if (!g || g.date !== h.date) {
      g = { date: h.date, hours: [] };
      groups.push(g);
    }
    g.hours.push(h);
  }
  return groups;
}

function minutesOfDay(hhmm: string): number | null {
  const parts = hhmm.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/**
 * Keep only the hours inside the doors window [open, close] (inclusive),
 * comparing by minute-of-day. When open/close is missing, returns every
 * hour (no window configured). A close earlier than open is treated as an
 * overnight window that wraps past midnight.
 */
export function hoursForDoorsWindow(
  hours: HourlyForecast[],
  open: string | null | undefined,
  close: string | null | undefined,
): HourlyForecast[] {
  if (!open || !close) return hours;
  const o = minutesOfDay(open);
  const c = minutesOfDay(close);
  if (o == null || c == null) return hours;
  return hours.filter((h) => {
    const hm = minutesOfDay(h.label);
    if (hm == null) return false;
    return c >= o ? hm >= o && hm <= c : hm >= o || hm <= c;
  });
}

/** Hide hours that have fully passed so today's strip starts near "now"
 * (like weeronline), while keeping every hour of future days. An hour is
 * kept until the end of that hour. */
export function visibleHours(hours: HourlyForecast[], nowMs: number): HourlyForecast[] {
  return hours.filter((h) => h.utcMs + 3_600_000 > nowMs);
}

/** Compact hour label for the timeline: "14:00" → "14", "09:00" → "9". */
export function formatHourLabel(label: string): string {
  const hh = label.split(":")[0];
  const n = Number(hh);
  return Number.isFinite(n) ? String(n) : label;
}

/** "20°" rounded, or "—" when unknown. */
export function formatTemp(t: number | null): string {
  return t == null ? "—" : `${Math.round(t)}°`;
}

/** Local calendar date "YYYY-MM-DD" for the given moment, in the runtime's
 * (device/browser) timezone — used to pick "today" for the home-screen
 * single-day view. */
export function todayLocalISO(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// ── Timeline composition (shared by the web + mobile cards) ─────────────────

export interface WeatherTimeline {
  dayGroups: HourlyDayGroup[];
  /** The day that should be expanded by default (today, else the first day). */
  defaultOpenDate: string | undefined;
}

/**
 * Build the card's per-day timeline from the raw hourly series: hide
 * fully-past hours, group by day, optionally keep only today (home screen),
 * trim each day to its doors window, and drop days that end up empty. Both
 * cards call this so the assembly rules live in one tested place.
 */
export function buildWeatherTimeline(opts: {
  hourly: HourlyForecast[] | null | undefined;
  nowMs: number;
  perDayDoors?: DayDoors[];
  fallbackDoors?: DoorsWindow;
  currentDayOnly?: boolean;
}): WeatherTimeline {
  const { hourly, nowMs, perDayDoors = [], fallbackDoors, currentDayOnly } = opts;
  if (!hourly?.length) return { dayGroups: [], defaultOpenDate: undefined };

  const today = todayLocalISO(new Date(nowMs));
  let groups = groupHoursByDay(visibleHours(hourly, nowMs));
  if (currentDayOnly) {
    const match = groups.find((g) => g.date === today);
    groups = match ? [match] : groups.slice(0, 1);
  }

  const dayGroups = groups
    .map((g) => {
      const doors = doorsForDay({
        perDay: perDayDoors,
        fallback: fallbackDoors ?? { doors_open: null, doors_close: null },
        eventDate: g.date,
      });
      return {
        date: g.date,
        hours: hoursForDoorsWindow(g.hours, doors.doors_open, doors.doors_close),
      };
    })
    .filter((g) => g.hours.length > 0);

  const defaultOpenDate = dayGroups.find((g) => g.date === today)?.date ?? dayGroups[0]?.date;
  return { dayGroups, defaultOpenDate };
}

/**
 * Whether a day should render collapsed. An explicit user choice (persisted
 * per platform) always wins; otherwise multi-day timelines default to
 * "only the default-open day expanded" and single-day timelines stay open.
 */
export function isDayCollapsed(
  date: string,
  opts: {
    collapsed: Record<string, boolean>;
    defaultOpenDate: string | undefined;
    dayCount: number;
    collapsible: boolean;
  },
): boolean {
  if (date in opts.collapsed) return opts.collapsed[date];
  return opts.collapsible && opts.dayCount > 1 ? date !== opts.defaultOpenDate : false;
}

/** Semantic icon keys — each platform maps these to a concrete icon. */
export type WeatherIconKey =
  | "sun"
  | "cloud-sun"
  | "cloud"
  | "cloud-fog"
  | "cloud-drizzle"
  | "cloud-rain"
  | "cloud-snow"
  | "cloud-lightning";

/** Map a WMO weather-interpretation code to a semantic icon key. */
export function weatherCodeToIconKey(code: number): WeatherIconKey {
  if (code === 0) return "sun";
  if (code <= 2) return "cloud-sun";
  if (code === 3) return "cloud";
  if (code <= 48) return "cloud-fog";
  if (code <= 57) return "cloud-drizzle";
  if (code <= 67) return "cloud-rain";
  if (code <= 77) return "cloud-snow";
  if (code <= 82) return "cloud-rain";
  if (code <= 86) return "cloud-snow";
  return "cloud-lightning";
}

/** Plain-English name for a weather code, e.g. "Partly cloudy". */
const ICON_LABELS: Record<WeatherIconKey, string> = {
  sun: "Clear",
  "cloud-sun": "Partly cloudy",
  cloud: "Cloudy",
  "cloud-fog": "Fog",
  "cloud-drizzle": "Drizzle",
  "cloud-rain": "Rain",
  "cloud-snow": "Snow",
  "cloud-lightning": "Thunderstorm",
};

/**
 * A readable condition for a WMO code, routed through the same code →
 * icon-key mapping the symbols use, so the word and the picture can never
 * describe different weather.
 */
export function weatherCodeToLabel(code: number): string {
  return ICON_LABELS[weatherCodeToIconKey(code)];
}

/** "12–20°" or "—" when temps are missing. Uses an en dash + degree sign. */
export function formatTempRange(min: number | null, max: number | null): string {
  if (min == null && max == null) return "—";
  if (min == null) return `${Math.round(max as number)}°`;
  if (max == null) return `${Math.round(min)}°`;
  return `${Math.round(min)}–${Math.round(max)}°`;
}

/**
 * Below this rain-probability (%), the hourly card hides the chance entirely.
 * Open-Meteo's hourly `precipitation_probability` is an ensemble-derived value
 * that regularly reports 20–40% for an hour whose weathercode is clear sky —
 * so a sunny cell showed a misleading "30%" next to a sun icon. Hiding low
 * probabilities keeps only meaningful rain chances. Tune here in one place.
 */
export const RAIN_CHANCE_DISPLAY_THRESHOLD = 40;

/**
 * "60%" rain chance, or null to hide it. Returns null when the probability is
 * unknown OR below RAIN_CHANCE_DISPLAY_THRESHOLD (low, noisy ensemble values
 * that contradict a clear-sky icon). The threshold is applied to the ROUNDED
 * value, so the rule matches exactly what's shown: anything that would display
 * as "40%" or higher is shown, everything below is hidden (e.g. 39.6 → "40%",
 * 39.4 → hidden).
 */
export function formatRainChance(probability: number | null): string | null {
  if (probability == null) return null;
  const rounded = Math.round(probability);
  if (rounded < RAIN_CHANCE_DISPLAY_THRESHOLD) return null;
  return `${rounded}%`;
}

/** Whether the cached forecast is old enough to warrant a background
 * refresh trigger from the client. Server cron refreshes hourly; the client
 * only nudges when it opens a clearly stale card (> 3h). */
export function isWeatherStale(fetchedAt: string, nowMs: number, maxAgeHours = 3): boolean {
  const fetched = Date.parse(fetchedAt);
  if (!Number.isFinite(fetched)) return true;
  return nowMs - fetched > maxAgeHours * 3_600_000;
}

/** Pretty weekday + day-month label for a forecast date, e.g. "Thu 23 Jul".
 * Reuses the shared formatShiftDay ("EEE d MMM") so day labels read the same
 * across briefing surfaces; falls back to the raw string when unparseable. */
export function formatForecastDate(isoDate: string): string {
  try {
    return formatShiftDay(isoDate) || isoDate;
  } catch {
    return isoDate; // formatShiftDay throws on an unparseable string
  }
}

/** The weather right now: the hour in progress, plus its day's range. */
export interface CurrentConditions {
  temperature: number | null;
  weatherCode: number;
  tempMin: number | null;
  tempMax: number | null;
}

/**
 * Pick out the conditions for `nowMs` from a cached forecast: the hourly
 * entry whose hour contains this moment, paired with that day's min/max.
 *
 * Shared by the home screen's weather tile and the full forecast screen,
 * so the "19° · 11–19°" they each show can never disagree. Returns null
 * when no cached hour covers this moment — a festival still in the
 * future, or a row that went stale — and callers then show nothing
 * rather than a temperature from the wrong hour.
 */
export function selectCurrentConditions(
  weather: Pick<BriefingWeather, "hourly" | "daily"> | null | undefined,
  nowMs: number,
): CurrentConditions | null {
  const hour = (weather?.hourly ?? []).find((h) => h.utcMs <= nowMs && nowMs < h.utcMs + 3_600_000);
  if (!hour) return null;

  const day = (weather?.daily ?? []).find((d) => d.date === hour.date);
  return {
    temperature: hour.temperature,
    weatherCode: hour.weatherCode,
    tempMin: day?.tempMin ?? null,
    tempMax: day?.tempMax ?? null,
  };
}
